// ── WHICH APH A WHEAT QUOTE IS PRICED ON — ONE RULE, THE CARD AND THE PAPER ───
//
// RMA approves and rates each APH database separately and the liabilities SUM —
// proved on Kennedy Farms' 2026: unit 0007 (57 bu x 240.52 ac -> $59,295) plus
// unit 0008 (59 x 149.26 -> $38,087) is his $97,382 coverage total to the dollar.
// `crop_coverage` can only report "average of N", and for a crop year with no
// acreage report yet (every CY2027 wheat row until Nov 15) the sync averages its
// databases UNWEIGHTED, giving dormant ground the same vote as the ground farmed.
//
// ⚠ AND THE DATABASES THE GROWER ACTUALLY PLANTED ARE THE ONES MISSING FROM IT.
// The AIP does not issue next year's approved yield on a database until last
// year's production is reported, and the sync keeps only P15s carrying an
// approved yield (api/nau-sync.js, `p15Entry.approvedYield > 0`). So the CY2027
// summary row averages exactly the ground he did NOT plant:
//
//   CLARK FIVE ENTERPRISES, Huntingdon PA wheat, measured 2026-09-18
//     planted 2026: 0003-0002 96 bu 49.1 ac · 0003-0006 103 bu 47.3 ac · 0003-0009 97 bu 38.75 ac
//     dormant:      0003-0003 65 (zero-acre since 2020) · 0004 74 · 0007 81 · 0008 80
//     CY2027 summary row: 75.0 = (65 + 74 + 81 + 80) / 4 — the four he did not plant
//     acre-weighted over what he farms: 98.7
//
// The card priced 98.7 and the printed quote priced 75.0 — a 56.3 bu guarantee
// against 74.0, $424.50 of MPCI coverage against $557.96, on the document the
// grower keeps. The screen had this rule since 2026-08-10 as closures inside the
// quoter; the print builder never received the database rows, so it read the
// summary. One module now, so the two cannot be built from different inputs.
//
// ── THE WEIGHTING RULE (Kevin, 2026-08-10) ───────────────────────────────────
// Weight by the unit+sub-unit's OWN acres for the quoted year where they exist,
// else by LAST YEAR'S acres for the same unit+sub-unit — "a starting point, a
// rough estimate". A database dormant last year therefore weighs nothing, which
// is right for the common case and wrong the year he moves wheat onto new
// ground; every caller prints which basis it used so that is visible.
//
// ⚠ ONE PLANTED DATABASE IS STILL A WEIGHTING. The screen's first cut refused to
// weight unless TWO databases carried acres, and fell back to the unweighted
// summary — the very number this rule exists to replace — whenever a grower
// planted wheat on a single database. Nothing documented why, and the comment
// above it said the plain average applies "when no acres can be found at all".
// Measured 2026-09-18: four CY2027 units in that shape (FAHNCKE Auglaize 87.3
// vs 95.0 planted, BERNARD D SMITH Blair 76.2 vs 82.0, PHILLIPS Dauphin 86.7 vs
// 90.0, MECKLEY Dauphin 86.6 vs 83.0). Now: any database with acres weights.
//
// ⚠ WHEAT ONLY, AND THAT IS A GUARD, NOT A GAP. Nothing here splits databases by
// TYPE, which is safe for wheat — measured 2026-09-18, every CY2026/27 wheat
// database is type 011 or 997 ("not stated" on the same ground) — and wrong for
// corn, where one county holds grain (016, bu) and silage (026, tons) databases
// and blending them is the Lismore defect CLAUDE.md records. The same UNWEIGHTED
// summary will reach corn and soybeans the day the AIP writes their CY2027
// coverages (none exist yet); widening this module to them needs a type split first.
//
// PURE and import-free, so tools/check-wheat-print-aph.js loads the SHIPPED file.

// SCO / ECO / ARPI rows are area overlays and carry no APH database of their own.
export const isWheatEndorsementPlan = (pl) => /SCO|ECO|ARPI/i.test(pl || "");

// Which database a row belongs to. Unit AND sub-unit — a unit holds several.
export const unitKeyOf = (u) => `${String((u && u.unit_number) || "").trim()}-${String((u && u.sub_unit) || "").trim()}`;

// A blank practice code is conventional (003), exactly as the syncs write it.
const practiceOf = (u) => (u && u.practice_code && u.practice_code !== "003" ? u.practice_code : "003");

// EVERY DATABASE THE FARM HOLDS for one county x practice — the quoted year's
// preferred, last year's filling the gaps. A database does not stop existing
// because this year's file has not restated it: Clark Five's three planted
// databases, and Kennedy's two (0007, 0008), are absent from the CY2027 file
// precisely because they were planted. Provenance is kept per row (`yieldsFrom`,
// `acFrom`) so a page can say which year a number came from.
//
// `yearOf(row)` returns the crop year of the policy the row belongs to.
//
// `planned` — { "0003-0002": 60, ... }, the acres an agent EXPECTS the grower to plant on
// each database in the quote year (Mike, 2026-09-18: "enter estimated planted acres for wheat
// CY2027 by unit so you can give a true estimated weighted average and price quote"). Last
// year's acres are a stand-in for exactly this, so once ANY unit carries a typed figure the
// typed figures ARE the weights, and a database left blank weighs nothing — planting
// intentions are the whole point. Nothing typed, nothing changes. `histAc` keeps the
// reported acres beside it for display.
export function unitDatabases(units, yearOf, { county, practice, curYear, planned = null } = {}) {
  const C = String(county || "").toUpperCase(), P = practice || "003";
  const plan = planned && Object.values(planned).some((v) => +v > 0) ? planned : null;
  const same = (u) => (u.crop || "") === "WHEAT" && String(u.county || "").toUpperCase() === C
    && !isWheatEndorsementPlan(u.plan) && !u.is_prevent_plant && practiceOf(u) === P;
  const by = {};
  for (const u of (units || [])) {
    if (!u || !same(u)) continue;
    const y = yearOf(u);
    if (y == null || (y !== curYear && y !== curYear - 1)) continue;
    const k = unitKeyOf(u), slot = (by[k] = by[k] || { k, cur: null, prev: null });
    if (y === curYear) slot.cur = u; else slot.prev = u;
  }
  return Object.values(by).map((s) => {
    const useCur = !!(s.cur && +s.cur.aph_yield > 0);
    const src = useCur ? s.cur : (s.prev || s.cur);
    if (!src || !(+src.aph_yield > 0)) return null;
    const ownAc = s.cur && +s.cur.acres > 0 ? +s.cur.acres : 0;
    const prevAc = s.prev && +s.prev.acres > 0 ? +s.prev.acres : 0;
    const histAc = ownAc || prevAc, histFrom = ownAc ? "own" : (prevAc ? "prev" : null);
    const pAc = plan ? (+plan[s.k] > 0 ? +plan[s.k] : 0) : null;
    return { k: s.k, u: src, ac: plan ? pAc : histAc, acFrom: plan ? (pAc > 0 ? "planned" : null) : histFrom,
             histAc, histFrom, yieldsFrom: useCur ? curYear : curYear - 1, priorYields: !useCur };
  }).filter(Boolean).sort((a, b) => (b.ac - a.ac) || a.k.localeCompare(b.k));
}

// Blend a SET of databases into one yield triplet. ONE selected is the degenerate
// case and does not go through the weighting — a single database with no acres
// would weigh zero and vanish, when "quote this unit" plainly means quote it.
// Otherwise weight by acres; when NONE of the set carries acres fall back to a
// straight average and SAY so (`basis: "equal"`), because an equal-weight answer
// labelled acre-weighted is a false statement.
//
// All three yields are weighted the same way or they stop describing one policy:
// the effective coverage level is approved / adjusted, so an acre-weighted
// approved against an unweighted adjusted invents a trend adjustment.
// ── yield_options: the options on each APH DATABASE (P15 f[38]) ──────────────────────────
// RMA P11-1 §14 rates a database above the top ADM level with a cubic surcharge when its list
// names QL, YE or YC, and without one when TA is elected alone (rdfSurchargeBranch in
// src/mpciEngine.js). A string is a KNOWN list ("" = none applied); null is unknown. Several
// databases blended into one quote take the UNION when all are known; with any unknown the
// union is still settled if a known one already names QL/YE/YC, and is otherwise null — the
// same rule the syncs apply (settleYieldOptions). Kept import-free on purpose: checkers load
// this module by source.
const SURCHARGE_OPTS = ["QL", "YE", "YC"];
export const yieldOptionsOf = (u) => (u && typeof u.yield_options === "string" ? u.yield_options : null);
export function yieldOptionsUnion(lists) {
  const set = new Set(); let unknown = false;
  for (const v of (lists || [])) {
    if (typeof v !== "string") { unknown = true; continue; }
    for (const t of v.toUpperCase().split(/[^A-Z]+/).filter(Boolean)) set.add(t);
  }
  const codes = [...set].sort();
  if (!unknown || codes.some((c) => SURCHARGE_OPTS.includes(c))) return (lists && lists.length) ? codes.join(",") : null;
  return null;
}

export function blendDatabases(dbs) {
  if (!dbs || !dbs.length) return null;
  const trio = (u) => ({ a: +u.aph_yield, r: (+u.rate_yield > 0 ? +u.rate_yield : +u.aph_yield),
    d: (+u.adjusted_yield > 0 ? +u.adjusted_yield : (+u.rate_yield > 0 ? +u.rate_yield : +u.aph_yield)) });
  // `members` — the databases that carried the result, with the acres each weighed. Kept
  // so a caller can state the production guarantee database by database and prove the
  // summary reconciles to it (see productionGuarantee below), rather than asserting it.
  const member = (x, w) => { const t = trio(x.u);
    return { k: x.k, aph: t.a, rate: t.r, adjusted: t.d, acres: w, priorYields: !!x.priorYields, yieldsFrom: x.yieldsFrom,
             yieldOptions: yieldOptionsOf(x.u) }; };
  if (dbs.length === 1) {
    const t = trio(dbs[0].u);
    return { aph: t.a, rate: t.r, adjusted: t.d, acres: +(dbs[0].ac || 0).toFixed(2), n: 1, basis: "single",
             yieldOptions: yieldOptionsOf(dbs[0].u),
             priorYieldCount: dbs[0].priorYields ? 1 : 0, members: [member(dbs[0], +(dbs[0].ac || 0))] };
  }
  let ac = 0, ay = 0, ry = 0, dy = 0;
  for (const x of dbs) { if (!(x.ac > 0)) continue; const t = trio(x.u); ac += x.ac; ay += t.a * x.ac; ry += t.r * x.ac; dy += t.d * x.ac; }
  if (ac > 0) {
    const planted = dbs.filter((x) => x.ac > 0);
    return { aph: +(ay / ac).toFixed(1), rate: +(ry / ac).toFixed(1), adjusted: +(dy / ac).toFixed(1),
             acres: +ac.toFixed(2), n: planted.length, yieldOptions: yieldOptionsUnion(planted.map((x) => yieldOptionsOf(x.u))),
             basis: dbs.some((x) => x.acFrom === "planned") ? "planned" : dbs.some((x) => x.acFrom === "prev") ? "prev" : "own",
             priorYieldCount: planted.filter((x) => x.priorYields).length,
             members: planted.map((x) => member(x, x.ac)) };
  }
  const t = dbs.map((x) => trio(x.u));
  const avg = (f) => +(t.reduce((s, v) => s + f(v), 0) / t.length).toFixed(1);
  return { aph: avg((v) => v.a), rate: avg((v) => v.r), adjusted: avg((v) => v.d), acres: 0, n: dbs.length, basis: "equal",
           yieldOptions: yieldOptionsUnion(dbs.map((x) => yieldOptionsOf(x.u))),
           priorYieldCount: dbs.filter((x) => x.priorYields).length, members: dbs.map((x) => member(x, 0)) };
}

// The whole operation, acre-weighted. Null — meaning "the stored summary stands" —
// when there is at most one database (the summary IS that database) or when no
// database carries acres in either year (nothing to weight by). Otherwise the
// blend, however many databases were planted: ONE counts (see the header).
//
// `dbCount` and `priorYieldCount` ride on the result so a page can state its
// basis: how many of the databases carried the weight, and how many of those
// are priced on LAST year's approved yield because the AIP has not issued this
// year's yet (Clark Five: 3 of 7, all 3 on 2026 yields).
export function weightedDatabaseAph(units, yearOf, opts) {
  const dbs = unitDatabases(units, yearOf, opts);
  if (dbs.length < 2) return null;
  const b = blendDatabases(dbs);
  if (!(b && b.acres > 0 && b.n >= 1)) return null;
  return { ...b, dbCount: dbs.length };
}

// The acres typed for the quote year across a county x practice's databases, or null where
// none were typed. Only databases that exist count, so a stale entry cannot inflate the
// enterprise acreage the premium is banded on.
export function plannedUnitAcres(units, yearOf, opts) {
  if (!opts || !opts.planned) return null;
  const dbs = unitDatabases(units, yearOf, opts).filter((d) => d.acFrom === "planned");
  const s = dbs.reduce((t, d) => t + d.ac, 0);
  return s > 0 ? +s.toFixed(2) : null;
}

// ── WHAT THE PRINTED APH IS BUILT ON, IN WORDS ───────────────────────────────
// A blended APH is only defensible if the page says it is one. Null where the figure
// is a single AIP approved yield (nothing to explain) — the caller's own provenance
// label (Estimated / prior-year) still applies on top.
//   Clark Five: "3 of 7 databases, weighted by 2026 acres; 3 on 2026 approved yields —
//               2027 not yet issued"
export function aphBasisNote({ source, blend, quoteYear } = {}) {
  if ((source !== "weighted" && source !== "selected") || !blend) return null;
  const Y = +quoteYear, plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  const parts = [source === "selected"
    ? `${plural(blend.n, "selected database")}`
    : `${blend.n} of ${blend.dbCount != null ? blend.dbCount : blend.n} databases`];
  if (blend.basis === "equal") parts.push("straight average, no acres to weight by");
  else if (blend.basis === "planned") parts.push(`weighted by the ${Y} acres entered`);
  else if (blend.n > 1) parts.push(`weighted by ${blend.basis === "prev" ? `${Y - 1} ` : ""}acres`);
  if (blend.priorYieldCount) parts.push(`${blend.priorYieldCount} on ${Y - 1} approved yield${blend.priorYieldCount === 1 ? "" : "s"} — ${Y} not yet issued`);
  return parts.join("; ");
}

// ── QUOTE EACH DATABASE, THEN COMBINE — how RMA and the AIP actually rate it ─
// A multi-database unit is NOT priced on one blended APH. RMA rounds each database's
// guarantee to the tenth (engine: guarBu = R(aph x cl, 1)), rates each on its own three
// yields, and the liabilities SUM. The APH database table on the card has always quoted
// this way ("verified against NAU's own quote at 240.52 / 389.78") while the columns beside
// it priced the blend — so one screen carried two answers. This makes the columns, the
// printed quote and the recommendation engine price the SAME per-database sum.
//
// Returns a function with quoteWheatRP2027's signature and result shape, or null where there
// is nothing to combine (fewer than two databases carrying acres — the ordinary quote IS it).
// Acres passed in still set the enterprise discount band for every database (it is keyed to
// the whole unit); each database's per-acre result is weighted by ITS acres.
//
// Clark Five at 75%: 72.0 / 77.3 / 72.8 bu/ac on 49.1 / 47.3 / 38.75 ac = 74.08 bu/ac — the
// figure the AIP billed his 2026 liability on (57,672 / 135.15 / $5.76 = 74.08), where the
// blended 98.7 x 75% gives 74.0.
export function quoteOverDatabases(members, quoteFn) {
  const rows = (members || []).filter((m) => m && +m.acres > 0 && +m.aph > 0);
  if (rows.length < 2 || typeof quoteFn !== "function") return null;
  const W = rows.reduce((s, m) => s + +m.acres, 0);
  const R = (v, d) => Math.round(v * 10 ** d) / 10 ** d;
  const wavg = (qs, f) => { let s = 0; for (let i = 0; i < qs.length; i++) { const v = f(qs[i]); if (v == null || !isFinite(+v)) return null; s += +v * rows[i].acres; } return s / W; };
  const fn = (args) => {
    const qs = rows.map((m) => quoteFn({ ...args, aph: m.aph, rateYield: m.rate, adjustedYield: m.adjusted,
      yieldOptions: typeof m.yieldOptions === "string" ? m.yieldOptions : (args.yieldOptions ?? null) }));
    if (qs.some((q) => !q)) return null;
    const A = +args.acres || W;
    const liabPerAc = wavg(qs, (q) => q.liabPerAc), grossPerAc = wavg(qs, (q) => q.grossPerAc);
    const netAcre = wavg(qs, (q) => q.netAcre), grossAcre = wavg(qs, (q) => q.grossAcre);
    const ppLiabPerAc = wavg(qs, (q) => q.ppLiabPerAc), ppGuarBu = wavg(qs, (q) => q.ppGuarBu);
    return { ...qs[0],
      guarBu: R(wavg(qs, (q) => q.guarBu), 2), liabPerAc, grossPerAc,
      premRate: liabPerAc > 0 ? grossPerAc / liabPerAc : qs[0].premRate,
      netAcre: R(netAcre, 2), grossAcre: R(grossAcre, 2),
      netTotal: Math.round(netAcre * A), grossTotal: Math.round(grossAcre * A), liabilityTotal: Math.round(liabPerAc * A),
      ppLiabPerAc: ppLiabPerAc == null ? null : R(ppLiabPerAc, 2), ppGuarBu: ppGuarBu == null ? null : R(ppGuarBu, 2),
      ppLiabilityTotal: ppLiabPerAc == null ? null : Math.round(ppLiabPerAc * A),
      effCl: Math.max(...qs.map((q) => +q.effCl || 0)), capExceeded: qs.some((q) => q.capExceeded),
      // §14 across the databases: undetermined if ANY is, and then the alternative is the same
      // acre-weighted sum with each undetermined database on its TA-alone figure.
      rdfBranch: qs.some((q) => q.rdfBranch === "unknown") ? "unknown"
        : qs.some((q) => q.rdfBranch === "surcharge") ? "surcharge"
        : qs.some((q) => q.rdfBranch === "none") ? "none" : "not-applicable",
      rdfBranchAlt: qs.some((q) => q.rdfBranch === "unknown")
        ? { branch: "none", netAcre: R(wavg(qs, (q) => (q.rdfBranchAlt ? q.rdfBranchAlt.netAcre : q.netAcre)), 2) } : null,
      perDatabase: rows.map((m, i) => ({ k: m.k, acres: m.acres, aph: m.aph, guarBu: qs[i].guarBu, liabPerAc: qs[i].liabPerAc, netAcre: qs[i].netAcre })),
    };
  };
  fn.databaseCount = rows.length;
  return fn;
}

// ── LAST YEAR'S GUARANTEE CHAIN — what the AIP billed, at the price RMA published ─
// The prior-year column used to BACK-SOLVE its price from the billed liability divided by
// the summary APH x level (426.73 / 74.0 = $5.77 on Clark Five), which folds RMA's
// per-database rounding into the price: $5.77 appears on no document he holds; the SOI and
// RMA both say $5.76. The method verified against seven real SOIs runs the other way —
// production guarantee = liability / elected price — so:
//   price       the stored election if the AIP wrote one, else RMA's PUBLISHED price x the
//               row's own price election (CAT 55%), else — only with nothing published — the
//               old back-solve, labelled as such
//   guarantee   billed liability / price (the AIP's own figure), else APH x level
export function priorYearChain({ storedElection = null, publishedPrice = null, priceElectionPct = 1, liabPerAc = null, aph = null, cl = null } = {}) {
  const pos = (v) => (v != null && isFinite(+v) && +v > 0 ? +v : null);
  const pe = pos(priceElectionPct) && +priceElectionPct <= 1 ? +priceElectionPct : 1;
  const r2 = (v) => Math.round(v * 100) / 100, r1 = (v) => Math.round(v * 10) / 10;
  const apc = pos(aph) && pos(cl) ? r1(+aph * +cl) : null;
  let price = null, priceSource = null;
  if (pos(storedElection)) { price = +storedElection; priceSource = "election"; }
  else if (pos(publishedPrice)) { price = r2(+publishedPrice * pe); priceSource = "published"; }
  else if (pos(liabPerAc) && apc) { price = r2(+liabPerAc / apc); priceSource = "back-solved"; }
  const guarBu = pos(liabPerAc) && price ? +liabPerAc / price : apc;
  return { price, priceSource, guarBu: guarBu == null ? null : Math.round(guarBu * 100) / 100,
           guarSource: pos(liabPerAc) && price ? "billed" : (apc ? "aph" : null) };
}

// ── THE PRODUCTION GUARANTEE, DATABASE BY DATABASE ───────────────────────────
// RMA: guarantee per acre = approved yield x coverage level, per APH database, and the
// liabilities SUM. A summary APH is defensible only if it reproduces that sum, so this
// computes both and returns the gap instead of asserting there is none:
//   perDb      each database's own approved yield x coverage, rounded to the tenth
//              RMA prints (Clark Five 2026: 72.0 / 77.3 / 72.8 on the AIP's own P11)
//   total      Σ acres x perDb — the production the policy actually guarantees
//   perAcre    total / Σ acres — the exact equivalent per acre
//   fromSummary the summary APH x coverage — what the page would print from one number
// `members` comes from blendDatabases. Null when nothing carries acres (no sum exists).
export function productionGuarantee(members, coverageLevel, summaryAph) {
  const cl = +coverageLevel;
  const rows = (members || []).filter((m) => m && +m.acres > 0 && +m.aph > 0);
  if (!rows.length || !(cl > 0)) return null;
  const r1 = (v) => Math.round(v * 10) / 10;
  const per = rows.map((m) => ({ k: m.k, acres: +m.acres, aph: +m.aph, guarPerAc: r1(+m.aph * cl) }));
  const ac = per.reduce((s, m) => s + m.acres, 0);
  const total = per.reduce((s, m) => s + m.acres * m.guarPerAc, 0);
  const perAcre = total / ac;
  const fromSummary = summaryAph != null ? +summaryAph * cl : null;
  return { perDb: per, acres: +ac.toFixed(2), totalBu: +total.toFixed(1), perAcre: +perAcre.toFixed(2),
           fromSummary: fromSummary != null ? +fromSummary.toFixed(2) : null,
           gapPerAcre: fromSummary != null ? +(fromSummary - perAcre).toFixed(3) : null };
}

// ── THE PRECEDENCE, IN ONE PLACE ─────────────────────────────────────────────
// Each candidate is computed by the caller; this only decides which one prices
// the quote. The card and every printed page call it, so they cannot rank the
// candidates differently.
//
//   edited    the agent recomputed the database from an edited yield history
//   selected  the agent ticked specific databases to quote
//   weighted  the AIP's own databases, acre-weighted (weightedDatabaseAph above)
//   rolled    last year's approved yield rolled forward by Silas once last
//             year's production is keyed and no current-year row exists
//   stored    the crop_coverage summary row
//
// RATE and ADJUSTED follow the same selection, or they stop describing the same
// thing. An applied edit returns them NULL: the caller derives both from the
// edited history, because the AIP's rate yield belongs to the database the edit
// replaced. A rolled APH keeps the stored rate/adjusted, as the card always has.
export function resolveQuoteYields({ stored, edited = null, editApplied = false, selected = null, weighted = null, rolled = null } = {}) {
  const st = stored || {};
  const pos = (v) => (v != null && +v > 0 ? +v : null);
  const source = (edited && edited.aph != null) ? "edited"
    : selected ? "selected"
    : weighted ? "weighted"
    : (rolled != null ? "rolled" : "stored");
  const aph = source === "edited" ? +edited.aph
    : source === "selected" ? selected.aph
    : source === "weighted" ? weighted.aph
    : source === "rolled" ? +rolled
    : (st.aph != null ? +st.aph : null);
  const trio = editApplied ? null : (selected || weighted || null);
  const rate = editApplied ? null : trio ? trio.rate : pos(st.rate);
  const adjusted = editApplied ? null : trio ? trio.adjusted : pos(st.adjusted);
  // The option list travels with the yields it describes: the selection's or the blend's, else
  // the stored row's. An applied edit is the agent's own history, so it has no AIP list — the
  // caller states the options it derived that history with.
  const yieldOptions = editApplied ? null
    : trio ? (typeof trio.yieldOptions === "string" ? trio.yieldOptions : null)
    : (typeof st.yieldOptions === "string" ? st.yieldOptions : null);
  return { aph, rate, adjusted, yieldOptions, source, blend: source === "selected" ? selected : source === "weighted" ? weighted : null };
}
